/**
 * Settings → Tags (web.md §4, fix 1: after Folders, `tag` icon): name, icon and how many messages carry
 * the tag. Delete (the shell's trash tool) removes the tag from every message and offers Undo.
 */

import type { Tag, TagId } from '@digester/core';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { IconField } from '../../icons/IconField.js';
import { formatCount } from '../../ui/format.js';
import { Field, Section } from '../../ui/form.js';
import { EditorEmpty, EditorFrame, useDraft, type ItemsResult, type SettingsCategory, type SettingsEditorProps } from '../shell/index.js';

interface TagDraft {
  name: string;
  icon: string;
}

function useItems(): ItemsResult {
  const q = useEngineQuery(qk.tags(), (engine) => engine.tags.list());
  return {
    items: (q.data ?? []).map((t) => ({ id: t.id, name: t.name, icon: t.icon })),
    isLoading: q.isLoading,
    error: q.error ?? null,
  };
}

/** Messages carrying the tag (any folder). Refreshed with the message lists. */
function useUsage(tagId: string | null): number | undefined {
  return useEngineQuery(
    ['list', 'tag-usage', tagId ?? ''],
    async (engine) =>
      tagId
        ? (await engine.messages.list({ scope: { kind: 'query', query: { type: 'group', operator: 'all', children: [{ type: 'condition', field: 'tag', operator: 'in', values: [tagId as TagId] }] } }, sort: 'newest', limit: 1 })).total
        : 0,
    { enabled: tagId !== null },
  ).data;
}

function TagEditor({ recordId }: SettingsEditorProps) {
  const q = useEngineQuery(qk.tags(), (engine) => engine.tags.list());
  if (!recordId) return <EditorEmpty />;
  if (q.isLoading) return <EditorEmpty text="Loading…" />;
  if (recordId === 'new') return <TagForm tag={null} initial={{ name: '', icon: 'tag' }} />;
  const tag = (q.data ?? []).find((t) => t.id === recordId);
  if (!tag) return <EditorEmpty />;
  return <TagForm tag={tag} initial={{ name: tag.name, icon: tag.icon }} />;
}

function TagForm({ tag, initial }: { tag: Tag | null; initial: TagDraft }) {
  const engine = useEngine();
  const usage = useUsage(tag?.id ?? null);
  const draft = useDraft<TagDraft>({
    initial,
    isNew: tag === null,
    onSave: async (v) => {
      const input = { name: v.name.trim(), icon: v.icon as never };
      const saved = tag ? await engine.tags.update(tag.id, input) : await engine.tags.create(input);
      return saved.id;
    },
  });
  const v = draft.value;
  return (
    <EditorFrame title={v.name.trim() || tag?.name || 'New tag'} icon={v.icon}>
      <Section title="Tag">
        <Field label="Name" name="name" value={v.name} required maxLength={80} onChange={(name) => draft.set((d) => ({ ...d, name }))} />
        <IconField label="Icon" name="icon" value={v.icon} onChange={(icon) => draft.set((d) => ({ ...d, icon }))} />
      </Section>
      {tag ? (
        <Section title="Usage">
          <div className="read-only-row" data-tag-usage>
            <span>Messages with this tag</span>
            <strong title={usage === undefined ? undefined : String(usage)}>{usage === undefined ? '—' : formatCount(usage)}</strong>
          </div>
          <p className="field-help">Deleting the tag removes it from every message. You can undo it right after.</p>
        </Section>
      ) : null}
    </EditorFrame>
  );
}

export const category: SettingsCategory = {
  id: 'tags',
  name: 'Tags',
  icon: 'tag',
  useItems,
  Editor: TagEditor,
  newLabel: 'New tag',
  archive: (engine, item) => engine.tags.delete(item.id as TagId),
  archiveNote: 'The tag is removed from every message. You can undo this.',
  archiveLabel: { action: 'Delete', done: 'Item deleted' },
};
